"use client";

import { createContactRecord } from "@/actions";
import { interestOptions } from "@/data/contact-interests";
import { ContactFormInputs, ContactFormSchema } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import Button from "./Button";
import posthog from "posthog-js";

export default function ContactForm() {
  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ContactFormInputs>({
    resolver: zodResolver(ContactFormSchema),
  });



  async function onSubmit(data: ContactFormInputs) {
    try {
      await createContactRecord(data);
      posthog.capture("contact_form_submitted", {
        interest: data["I am"]?.value,
      });
      reset();
    } catch (error) {
      if (error instanceof Error) {
        setError("root", { message: error.message });
        posthog.capture("contact_form_error", { error_message: error.message });
        posthog.captureException(error);
      }
      console.error(error);
    }
  }

  useEffect(() => {}, []);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="top-0 right-0 z-30 ml-auto flex h-full w-full flex-col justify-between gap-2 px-6 py-12 md:w-2/3 lg:w-1/2 lg:py-6 lg:pr-7.5"
    >
      <h2 className="text-primary text-center text-[32px] font-semibold md:hidden">
        GET IN TOUCH
      </h2>{" "}
      <div className="space-y-4">
        <div className="">
          <label htmlFor="name" className="text-xl/loose font-semibold">
            Full Name
          </label>
          <input
            type="text"
            className="border-primary h-12.5 w-full rounded-sm border-b-2 bg-white/90 px-2.5 placeholder:text-gray-400"
            placeholder="Please enter your name"
            {...register("Name")}
          />
          {errors.Name && (
            <p id="name-error" className="mt-1 text-sm text-red-500">
              {errors.Name.message?.toString()}
            </p>
          )}
        </div>

        <div className="">
          <label htmlFor="email" className="text-xl/loose font-semibold">
            Email
          </label>
          <input
            type="email"
            className="border-primary h-12.5 w-full rounded-sm border-b-2 bg-white/90 px-2.5 placeholder:text-gray-400"
            placeholder="Please enter your email"
            {...register("Email")}
          />
          {errors.Email && (
            <p id="email-error" className="mt-1 text-sm text-red-500">
              {errors.Email.message?.toString()}
            </p>
          )}
        </div>

        <div className="">
          <label
            htmlFor="react-select-interest-select-input"
            className="text-xl/loose font-semibold"
          >
            I am:
          </label>
          <Controller
            name="I am"
            control={control}
            render={({ field }) => (
              <Select
                id="interest-select"
                {...field}
                options={interestOptions.map((option) => ({
                  value: option.value,
                  label: option.value,
                }))}
                unstyled
                isSearchable={false}
                placeholder="Please select an option"
                maxMenuHeight={200}
                menuPosition="absolute"
                instanceId={"interest-select"}
                classNames={{
                  control: () =>
                    "border-primary h-12.5 w-full rounded-sm border-b-2 bg-white/90 px-2.5",
                  input: () => "hidden",
                  option: () =>
                    "text-gray-500 text-sm px-2 py-1 bg-gray-50 hover:bg-secondary-light rounded-sm hover:text-foreground",
                  menu: () => "bg-white rounded-b-sm p-2 z-40",
                  placeholder: () => "text-gray-400",
                  menuList: () =>
                    "space-y-2 scrollbar-thin scrollbar-thumb-primary pr-2",
                }}
              />
            )}
          />
          {errors["I am"] && (
            <p id="interest-error" className="mt-1 text-sm text-red-500">
              {errors["I am"].message?.toString()}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="notes" className="text-xl/loose font-semibold">
            Message
          </label>
          <textarea
            id="notes"
            className="border-primary scrollbar-thin scrollbar-thumb-secondary w-full resize-none rounded-sm border-b-2 bg-white p-2.5 placeholder:text-gray-400"
            rows={4}
            placeholder="Your message here..."
            {...register("Notes")}
          />
          {errors.Notes && (
            <p id="notes-error" className="text-sm text-red-500">
              {errors.Notes.message?.toString()}
            </p>
          )}
        </div>
      </div>
      <Button
        color="primary"
        className="mt-2 max-w-max text-xl font-semibold"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending…" : "Send"}
      </Button>
      {errors.root && (
        <p className="mt-2 text-center text-red-700">
          {errors.root.message?.toString()}
        </p>
      )}
      {isSubmitSuccessful && (
        <p className="mt-2 text-center text-green-700">
          Thanks! We’ll be in touch shortly.
        </p>
      )}
    </form>
  );
}
